import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ClipboardList,
  Eye,
  FileClock,
  FileSearch,
  FilterX,
  Info,
  Loader2,
  RefreshCw,
  Search,
  Shield,
  Users,
  X,
} from "lucide-react";
import api from "../../api";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../../../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";

interface EventOption {
  id: string;
  name: string;
}

type AcademicStatusCode =
  | "PENDING_CLEAN"
  | "PENDING_ASSIGNMENT"
  | "UNDER_EVALUATION"
  | "PENDING_DECISION"
  | "OBSERVATIONS"
  | "APPROVED"
  | "REJECTED"
  | "FINAL_MATERIAL_PENDING"
  | "FINALIZED";

type DocumentStatusCode =
  | "ORIGINAL_ONLY"
  | "PENDING_CLEAN"
  | "CLEAN_AVAILABLE"
  | "CORRECTION_PENDING"
  | "FINAL_MATERIAL_PENDING"
  | "FINAL_MATERIAL_RECEIVED";

interface SummaryResponse {
  total: number;
  pendingClean: number;
  pendingAssignment: number;
  underEvaluation: number;
  pendingDecision: number;
  withObservations: number;
  approved: number;
  rejected: number;
  finalMaterialPending: number;
  hasAssignedEvents: boolean;
}

interface MonitorPaper {
  id: string;
  folio: string;
  title: string;
  author: string;
  event: { id: string; name: string; shortName: string | null };
  academicStatus: { code: AcademicStatusCode; label: string };
  registrationStatus: string;
  evaluationStatus: string | null;
  currentRound: number;
  assignedEvaluators: number;
  submittedEvaluations: number;
  expectedEvaluations: number;
  documentStatus: { code: DocumentStatusCode; label: string };
  updatedAt: string;
}

interface ListResponse {
  items: MonitorPaper[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasAssignedEvents: boolean;
}

interface PaperDetail {
  id: string;
  folio: string;
  title: string;
  event: { id: string; name: string; shortName: string | null };
  author: { name: string; emailMasked: string | null; institution: string | null };
  receivedAt: string;
  registrationStatus: string;
  academicStatus: { code: AcademicStatusCode; label: string };
  documentStatus: { code: DocumentStatusCode; label: string };
  currentRound: number;
  academic: {
    assignedEvaluators: number;
    submittedEvaluations: number;
    pendingEvaluations: number;
    evaluationStatus: string | null;
    latestEvaluationReceivedAt: string | null;
    decisionSummary: Array<{ roundNumber: number; summary: string | null; completedAt: string | null }>;
    evaluators: Array<{
      id: string;
      name: string;
      emailMasked: string | null;
      institution: string | null;
      status: "SUBMITTED" | "PENDING";
      decision: string | null;
      submittedAt: string | null;
    }>;
  };
  documents: Array<{
    id: string;
    versionNumber: number;
    versionType: string;
    roundNumber: number | null;
    archivoNombre: string;
    archivoMime: string;
    uploadedAt: string;
    status: string;
  }>;
  observations: {
    finalDecisionStatus: string | null;
    finalDecisionLabel: string | null;
    finalDecisionAt: string | null;
    reviewComments: string | null;
    evaluatorComments: Array<{
      evaluatorName: string;
      roundNumber: number;
      decision: string | null;
      comments: string | null;
      submittedAt: string | null;
    }>;
  };
  timeline: Array<{ label: string; at: string | null; status?: string }>;
}

interface PaperEvaluationMonitorPageProps {
  canView: boolean;
  selectedEventId: string;
  availableEvents: EventOption[];
}

const primaryButtonClass = "bg-[#1e3a5f] text-white hover:bg-[#0f2744] hover:text-white disabled:text-white";

const academicFilterOptions: Array<{ value: AcademicStatusCode; label: string }> = [
  { value: "PENDING_CLEAN", label: "Pendiente de CLEAN" },
  { value: "PENDING_ASSIGNMENT", label: "Pendiente de asignacion" },
  { value: "UNDER_EVALUATION", label: "En evaluacion" },
  { value: "PENDING_DECISION", label: "Dictamen pendiente" },
  { value: "OBSERVATIONS", label: "Con observaciones" },
  { value: "APPROVED", label: "Aprobada" },
  { value: "REJECTED", label: "Rechazada" },
  { value: "FINAL_MATERIAL_PENDING", label: "Material final pendiente" },
];

const documentFilterOptions: Array<{ value: DocumentStatusCode; label: string }> = [
  { value: "PENDING_CLEAN", label: "Pendiente de CLEAN" },
  { value: "CLEAN_AVAILABLE", label: "CLEAN disponible" },
  { value: "CORRECTION_PENDING", label: "Correccion pendiente" },
  { value: "FINAL_MATERIAL_PENDING", label: "Material final pendiente" },
  { value: "FINAL_MATERIAL_RECEIVED", label: "Material final recibido" },
];

const academicBadgeClasses: Record<AcademicStatusCode, string> = {
  PENDING_CLEAN: "border-amber-200 bg-amber-50 text-amber-700",
  PENDING_ASSIGNMENT: "border-orange-200 bg-orange-50 text-orange-700",
  UNDER_EVALUATION: "border-blue-200 bg-blue-50 text-blue-700",
  PENDING_DECISION: "border-violet-200 bg-violet-50 text-violet-700",
  OBSERVATIONS: "border-yellow-200 bg-yellow-50 text-yellow-700",
  APPROVED: "border-emerald-200 bg-emerald-50 text-emerald-700",
  REJECTED: "border-red-200 bg-red-50 text-red-700",
  FINAL_MATERIAL_PENDING: "border-slate-200 bg-slate-50 text-slate-700",
  FINALIZED: "border-green-200 bg-green-50 text-green-700",
};

const documentBadgeClasses: Record<DocumentStatusCode, string> = {
  ORIGINAL_ONLY: "border-slate-200 bg-slate-50 text-slate-700",
  PENDING_CLEAN: "border-amber-200 bg-amber-50 text-amber-700",
  CLEAN_AVAILABLE: "border-sky-200 bg-sky-50 text-sky-700",
  CORRECTION_PENDING: "border-orange-200 bg-orange-50 text-orange-700",
  FINAL_MATERIAL_PENDING: "border-slate-200 bg-slate-50 text-slate-700",
  FINAL_MATERIAL_RECEIVED: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

function formatDate(value: string | null | undefined, includeTime = true): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("es-MX", {
    day: "2-digit",
    month: "short",
    year: includeTime ? undefined : "numeric",
    hour: includeTime ? "2-digit" : undefined,
    minute: includeTime ? "2-digit" : undefined,
  }).format(date);
}

function KpiCard({
  title,
  value,
  icon: Icon,
  tone,
}: {
  title: string;
  value: number;
  icon: typeof ClipboardList;
  tone: string;
}) {
  return (
    <Card className="border-gray-200 shadow-sm">
      <CardContent className="flex min-h-[92px] items-center justify-between gap-3 p-4">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tone}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-snug text-[#0f2744]">{title}</p>
          <p className="mt-1 text-2xl font-bold text-[#0f2744]">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function AcademicBadge({ status }: { status: { code: AcademicStatusCode; label: string } }) {
  return <Badge variant="outline" className={academicBadgeClasses[status.code]}>{status.label}</Badge>;
}

function DocumentBadge({ status }: { status: { code: DocumentStatusCode; label: string } }) {
  return <Badge variant="outline" className={documentBadgeClasses[status.code]}>{status.label}</Badge>;
}

export function PaperEvaluationMonitorPage({ canView, selectedEventId, availableEvents }: PaperEvaluationMonitorPageProps) {
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [papers, setPapers] = useState<MonitorPaper[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [eventId, setEventId] = useState("ALL");
  const [academicStatus, setAcademicStatus] = useState("ALL");
  const [round, setRound] = useState("ALL");
  const [documentStatus, setDocumentStatus] = useState("ALL");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PaperDetail | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  useEffect(() => {
    if (selectedEventId && availableEvents.length === 1) {
      setEventId(selectedEventId);
    }
  }, [availableEvents.length, selectedEventId]);

  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page,
      pageSize,
      sortBy: "updatedAt",
      sortDirection: "desc",
    };
    if (eventId !== "ALL") params.eventId = eventId;
    if (academicStatus !== "ALL") params.academicStatus = academicStatus;
    if (round !== "ALL") params.round = round;
    if (documentStatus !== "ALL") params.documentStatus = documentStatus;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (q.trim().length >= 3) params.q = q.trim();
    return params;
  }, [academicStatus, dateFrom, dateTo, documentStatus, eventId, page, pageSize, q, round]);

  const summaryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (eventId !== "ALL") params.eventId = eventId;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    return params;
  }, [dateFrom, dateTo, eventId]);

  const loadMonitor = useCallback(async () => {
    if (!canView) return;
    setIsLoading(true);
    setError(null);
    try {
      const [summaryResponse, listResponse] = await Promise.all([
        api.get<SummaryResponse>("/admin/papers-monitor/summary", { params: summaryParams, headers: { "X-Passive-Request": "true" } }),
        api.get<ListResponse>("/admin/papers-monitor", { params: queryParams, headers: { "X-Passive-Request": "true" } }),
      ]);
      setSummary(summaryResponse.data);
      setPapers(listResponse.data.items);
      setTotal(listResponse.data.total);
      setTotalPages(listResponse.data.totalPages);
    } catch {
      setError("No se pudo cargar el monitor.");
      setPapers([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [canView, queryParams, summaryParams]);

  useEffect(() => {
    loadMonitor();
  }, [loadMonitor]);

  const loadDetail = async (registrationId: string) => {
    setDetailId(registrationId);
    setIsDetailLoading(true);
    setDetail(null);
    try {
      const { data } = await api.get<PaperDetail>(`/admin/papers-monitor/${registrationId}`, {
        headers: { "X-Passive-Request": "true" },
      });
      setDetail(data);
    } catch {
      setDetail(null);
    } finally {
      setIsDetailLoading(false);
    }
  };

  const clearFilters = () => {
    setEventId(availableEvents.length === 1 && selectedEventId ? selectedEventId : "ALL");
    setAcademicStatus("ALL");
    setRound("ALL");
    setDocumentStatus("ALL");
    setQ("");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  const hasShortSearch = q.trim().length > 0 && q.trim().length < 3;
  const hasFilters = eventId !== "ALL" || academicStatus !== "ALL" || round !== "ALL" || documentStatus !== "ALL" || q.trim().length >= 3 || dateFrom || dateTo;
  const hasAssignedEvents = summary?.hasAssignedEvents ?? availableEvents.length > 0;

  if (!canView) {
    return (
      <EmptyState
        icon={<Shield className="h-16 w-16 text-slate-500" />}
        title="Acceso restringido"
        description="No tienes permisos para visualizar el Monitor de Ponencias."
      />
    );
  }

  if (!hasAssignedEvents && !isLoading && !error) {
    return (
      <div className="space-y-5">
        <PageHeader onRefresh={loadMonitor} isLoading={isLoading} />
        <EmptyState
          icon={<FileClock className="h-16 w-16 text-slate-400" />}
          title="Sin eventos asignados"
          description="No tienes eventos asignados para monitorear ponencias."
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader onRefresh={loadMonitor} isLoading={isLoading} />

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
        <KpiCard title="Total de ponencias" value={summary?.total ?? 0} icon={ClipboardList} tone="bg-blue-100 text-blue-700" />
        <KpiCard title="Pendientes de CLEAN" value={summary?.pendingClean ?? 0} icon={FileClock} tone="bg-amber-100 text-amber-700" />
        <KpiCard title="Pendientes de asignacion" value={summary?.pendingAssignment ?? 0} icon={Users} tone="bg-orange-100 text-orange-700" />
        <KpiCard title="En evaluacion" value={summary?.underEvaluation ?? 0} icon={FileSearch} tone="bg-sky-100 text-sky-700" />
        <KpiCard title="Dictamen pendiente" value={summary?.pendingDecision ?? 0} icon={ClipboardList} tone="bg-violet-100 text-violet-700" />
        <KpiCard title="Con observaciones" value={summary?.withObservations ?? 0} icon={Info} tone="bg-yellow-100 text-yellow-700" />
        <KpiCard title="Aprobadas" value={summary?.approved ?? 0} icon={CheckCircle2} tone="bg-emerald-100 text-emerald-700" />
        <KpiCard title="Rechazadas" value={summary?.rejected ?? 0} icon={X} tone="bg-red-100 text-red-700" />
        <KpiCard title="Material final pendiente" value={summary?.finalMaterialPending ?? 0} icon={FileClock} tone="bg-slate-100 text-slate-700" />
      </div>

      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-4">
          <div className="grid gap-4 lg:grid-cols-4">
            <FilterSelect label="Evento" value={eventId} onChange={(value) => { setEventId(value); setPage(1); }}>
              <SelectItem value="ALL">Todos los eventos</SelectItem>
              {availableEvents.map((event) => <SelectItem key={event.id} value={event.id}>{event.name}</SelectItem>)}
            </FilterSelect>
            <FilterSelect label="Estado academico" value={academicStatus} onChange={(value) => { setAcademicStatus(value); setPage(1); }}>
              <SelectItem value="ALL">Todos</SelectItem>
              {academicFilterOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
            </FilterSelect>
            <FilterSelect label="Ronda" value={round} onChange={(value) => { setRound(value); setPage(1); }}>
              <SelectItem value="ALL">Todas</SelectItem>
              <SelectItem value="1">Ronda 1</SelectItem>
              <SelectItem value="2">Ronda 2</SelectItem>
            </FilterSelect>
            <FilterSelect label="Estado documental" value={documentStatus} onChange={(value) => { setDocumentStatus(value); setPage(1); }}>
              <SelectItem value="ALL">Todos</SelectItem>
              {documentFilterOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
            </FilterSelect>
            <div className="space-y-2 lg:col-span-2">
              <Label>Buscar por folio, titulo o autor</Label>
              <div className="relative">
                <Input value={q} onChange={(event) => { setQ(event.target.value); setPage(1); }} placeholder="Buscar por folio, titulo o autor..." className="pr-9" />
                <Search className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
              {hasShortSearch && <p className="text-xs text-amber-700">Escribe al menos 3 caracteres para buscar.</p>}
            </div>
            <div className="space-y-2">
              <Label>Desde</Label>
              <Input type="date" value={dateFrom} onChange={(event) => { setDateFrom(event.target.value); setPage(1); }} />
            </div>
            <div className="space-y-2">
              <Label>Hasta</Label>
              <Input type="date" value={dateTo} onChange={(event) => { setDateTo(event.target.value); setPage(1); }} />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={clearFilters}>
              <FilterX className="mr-2 h-4 w-4" />
              Limpiar filtros
            </Button>
            <Button onClick={loadMonitor} disabled={isLoading} className={primaryButtonClass}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Actualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-gray-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-[#0f2744]">Ponencias recientes</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <EmptyState
              icon={<AlertCircle className="h-16 w-16 text-red-500" />}
              title="No se pudo cargar el monitor"
              description="Intenta actualizar la informacion. Si el problema persiste, revisa los logs del servidor."
              action={<Button className={primaryButtonClass} onClick={loadMonitor}>Reintentar carga</Button>}
            />
          ) : isLoading ? (
            <div className="flex min-h-[260px] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
            </div>
          ) : papers.length === 0 ? (
            <EmptyState
              icon={<FileSearch className="h-16 w-16 text-slate-400" />}
              title={hasFilters ? "No se encontraron resultados" : "Sin ponencias registradas"}
              description={hasFilters ? "No hay ponencias que coincidan con los filtros seleccionados." : "Cuando se reciban ponencias para este evento, apareceran aqui."}
              action={
                <div className="flex gap-2">
                  {hasFilters && <Button variant="outline" onClick={clearFilters}>Limpiar filtros</Button>}
                  <Button className={primaryButtonClass} onClick={loadMonitor}>Actualizar</Button>
                </div>
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Folio</TableHead>
                      <TableHead>Titulo</TableHead>
                      <TableHead>Autor</TableHead>
                      <TableHead>Evento</TableHead>
                      <TableHead>Estado academico</TableHead>
                      <TableHead>Ronda</TableHead>
                      <TableHead>Evaluadores</TableHead>
                      <TableHead>Evaluaciones</TableHead>
                      <TableHead>Documento</TableHead>
                      <TableHead>Actualizado</TableHead>
                      <TableHead>Accion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {papers.map((paper) => (
                      <TableRow key={paper.id}>
                        <TableCell className="whitespace-nowrap font-medium text-[#0f2744]">{paper.folio}</TableCell>
                        <TableCell className="max-w-[240px] truncate" title={paper.title}>{paper.title}</TableCell>
                        <TableCell className="whitespace-nowrap">{paper.author}</TableCell>
                        <TableCell className="max-w-[220px] truncate">{paper.event.shortName || paper.event.name}</TableCell>
                        <TableCell><AcademicBadge status={paper.academicStatus} /></TableCell>
                        <TableCell>{paper.currentRound ? `Ronda ${paper.currentRound}` : "-"}</TableCell>
                        <TableCell>{paper.assignedEvaluators} asignados</TableCell>
                        <TableCell>{paper.submittedEvaluations}/{paper.expectedEvaluations}</TableCell>
                        <TableCell><DocumentBadge status={paper.documentStatus} /></TableCell>
                        <TableCell className="whitespace-nowrap">{formatDate(paper.updatedAt)}</TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" onClick={() => loadDetail(paper.id)}>
                            <Eye className="mr-1 h-3.5 w-3.5" />
                            Ver
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-gray-600">Mostrando {papers.length} de {total} ponencias</p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((value) => Math.max(value - 1, 1))}>Anterior</Button>
                  <span className="text-sm text-gray-600">Pagina {page} de {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((value) => value + 1)}>Siguiente</Button>
                  <Select value={String(pageSize)} onValueChange={(value) => { setPageSize(Number(value)); setPage(1); }}>
                    <SelectTrigger className="w-[90px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Alert className="border-blue-200 bg-white text-blue-900">
        <Shield className="h-4 w-4" />
        <AlertDescription>Solo se muestran metadatos. Las rutas internas, tokens y archivos no se exponen desde este monitor.</AlertDescription>
      </Alert>

      <PaperDetailSheet
        detailId={detailId}
        detail={detail}
        isLoading={isDetailLoading}
        onClose={() => { setDetailId(null); setDetail(null); }}
      />
    </div>
  );
}

function PageHeader({ onRefresh, isLoading }: { onRefresh: () => void; isLoading: boolean }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#0f2744]">Monitor de Ponencias</h1>
            <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">Solo lectura</Badge>
          </div>
          <p className="mt-1 text-sm text-gray-600">Seguimiento academico y documental de ponencias por evento.</p>
        </div>
        <Button onClick={onRefresh} disabled={isLoading} className={primaryButtonClass}>
          {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Actualizar
        </Button>
      </div>
      <Alert className="border-blue-200 bg-blue-50 text-blue-900">
        <Info className="h-4 w-4" />
        <AlertDescription>Este monitor es de solo lectura. No modifica ponencias, evaluaciones, asignaciones ni documentos.</AlertDescription>
      </Alert>
    </div>
  );
}

function FilterSelect({ label, value, onChange, children }: { label: string; value: string; onChange: (value: string) => void; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
    </div>
  );
}

function EmptyState({ icon, title, description, action }: { icon: ReactNode; title: string; description: string; action?: ReactNode }) {
  return (
    <Card className="border-gray-200 shadow-sm">
      <CardContent className="flex min-h-[360px] flex-col items-center justify-center p-8 text-center">
        <div className="mb-5">{icon}</div>
        <h2 className="text-xl font-bold text-[#0f2744]">{title}</h2>
        <p className="mt-2 max-w-md text-sm text-gray-600">{description}</p>
        {action && <div className="mt-5">{action}</div>}
      </CardContent>
    </Card>
  );
}

function PaperDetailSheet({
  detailId,
  detail,
  isLoading,
  onClose,
}: {
  detailId: string | null;
  detail: PaperDetail | null;
  isLoading: boolean;
  onClose: () => void;
}) {
  return (
    <Sheet open={Boolean(detailId)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-3xl xl:max-w-4xl">
        <SheetHeader>
          <SheetTitle className="text-xl text-[#0f2744]">Detalle de ponencia</SheetTitle>
          <SheetDescription>Informacion de solo lectura</SheetDescription>
        </SheetHeader>
        {isLoading ? (
          <div className="flex min-h-[360px] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
          </div>
        ) : detail ? (
          <div className="space-y-4 px-4 pb-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-[#0f2744]">{detail.folio}</span>
              <AcademicBadge status={detail.academicStatus} />
              <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-700">Ronda {detail.currentRound}</Badge>
              <DocumentBadge status={detail.documentStatus} />
            </div>

            <DetailSection title="1. Datos generales">
              <DetailGrid rows={[
                ["Folio", detail.folio],
                ["Titulo", detail.title],
                ["Evento", detail.event.name],
                ["Autor principal", detail.author.name],
                ["Institucion", detail.author.institution || "-"],
                ["Fecha de recepcion", formatDate(detail.receivedAt)],
                ["Estado actual", detail.academicStatus.label],
                ["Ronda actual", `Ronda ${detail.currentRound}`],
              ]} />
            </DetailSection>

            <DetailSection title="2. Estado academico">
              <div className="grid gap-3 md:grid-cols-4">
                <MiniMetric label="Evaluadores asignados" value={String(detail.academic.assignedEvaluators)} />
                <MiniMetric label="Evaluaciones recibidas" value={`${detail.academic.submittedEvaluations}/${detail.academic.assignedEvaluators}`} />
                <MiniMetric label="Evaluaciones pendientes" value={String(detail.academic.pendingEvaluations)} />
                <MiniMetric label="Ultima evaluacion recibida" value={formatDate(detail.academic.latestEvaluationReceivedAt)} />
              </div>
              <div className="mt-4 grid gap-4 lg:grid-cols-2">
                <div className="rounded-md border border-gray-200">
                  {detail.academic.evaluators.length === 0 ? (
                    <p className="p-3 text-sm text-gray-600">No hay evaluadores asignados.</p>
                  ) : detail.academic.evaluators.map((evaluator) => (
                    <div key={evaluator.id} className="flex items-center justify-between gap-3 border-b border-gray-100 p-3 last:border-b-0">
                      <div>
                        <p className="text-sm font-medium text-[#0f2744]">{evaluator.name}</p>
                        <p className="text-xs text-gray-500">{evaluator.emailMasked || "-"}</p>
                      </div>
                      <Badge variant="outline" className={evaluator.status === "SUBMITTED" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}>
                        {evaluator.status === "SUBMITTED" ? "Evaluacion enviada" : "Pendiente"}
                      </Badge>
                    </div>
                  ))}
                </div>
                <div className="rounded-md border border-gray-200 p-3">
                  <p className="text-sm font-semibold text-[#0f2744]">Resumen de evaluaciones</p>
                  <div className="mt-3 space-y-2">
                    {detail.academic.decisionSummary.length === 0 ? (
                      <p className="text-sm text-gray-600">No hay dictamen final registrado.</p>
                    ) : detail.academic.decisionSummary.map((summary) => (
                      <div key={summary.roundNumber} className="rounded-md bg-gray-50 p-2 text-sm text-gray-700">
                        <p className="font-medium">Ronda {summary.roundNumber}</p>
                        <p className="whitespace-pre-wrap">{summary.summary || "Sin resumen registrado."}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </DetailSection>

            <DetailSection title="3. Versiones / documentos">
              <div className="overflow-x-auto rounded-md border border-gray-200">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Version</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Archivo</TableHead>
                      <TableHead>Fecha de carga</TableHead>
                      <TableHead>Estado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detail.documents.map((document) => (
                      <TableRow key={document.id}>
                        <TableCell>v{document.versionNumber}</TableCell>
                        <TableCell>{document.versionType}</TableCell>
                        <TableCell className="max-w-[260px] truncate">{document.archivoNombre}</TableCell>
                        <TableCell>{formatDate(document.uploadedAt)}</TableCell>
                        <TableCell>{document.status}</TableCell>
                      </TableRow>
                    ))}
                    {detail.documents.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-sm text-gray-600">No hay versiones documentales registradas.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <Alert className="mt-3 border-blue-200 bg-blue-50 text-blue-900">
                <Info className="h-4 w-4" />
                <AlertDescription>Este monitor solo muestra metadatos. No permite descargar documentos.</AlertDescription>
              </Alert>
            </DetailSection>

            <div className="grid gap-4 lg:grid-cols-2">
              <DetailSection title="4. Observaciones / dictamen">
                {detail.observations.finalDecisionLabel ? (
                  <div className="rounded-md border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
                    <p className="font-semibold">Dictamen final: {detail.observations.finalDecisionLabel}</p>
                    {detail.observations.finalDecisionAt && (
                      <p className="mt-1 text-xs text-blue-700">Emitido: {formatDate(detail.observations.finalDecisionAt)}</p>
                    )}
                  </div>
                ) : (
                  <div className="rounded-md border border-amber-100 bg-amber-50 p-3 text-sm text-amber-800">No hay dictamen final registrado.</div>
                )}
                {detail.observations.reviewComments ? (
                  <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap rounded-md border border-amber-100 bg-amber-50 p-3 text-xs text-amber-900">{detail.observations.reviewComments}</pre>
                ) : detail.observations.finalDecisionLabel ? (
                  <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">No hay observaciones finales adicionales.</div>
                ) : null}
                {detail.observations.evaluatorComments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {detail.observations.evaluatorComments.map((comment, index) => (
                      <div key={`${comment.evaluatorName}-${index}`} className="rounded-md border border-gray-200 p-3">
                        <p className="text-xs font-semibold text-gray-500">Ronda {comment.roundNumber} - {comment.decision || "Sin decision"}</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{comment.comments}</p>
                      </div>
                    ))}
                  </div>
                )}
              </DetailSection>

              <DetailSection title="5. Timeline academico-documental">
                <div className="space-y-3">
                  {detail.timeline.map((item, index) => (
                    <div key={`${item.label}-${index}`} className="flex gap-3">
                      <div className="mt-1 h-2.5 w-2.5 rounded-full bg-blue-500" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#0f2744]">{item.label}</p>
                        <p className="text-xs text-gray-500">{formatDate(item.at)}</p>
                      </div>
                      {item.status && <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-700">{item.status}</Badge>}
                    </div>
                  ))}
                </div>
              </DetailSection>
            </div>

            <Alert className="border-blue-200 bg-white text-blue-900">
              <Shield className="h-4 w-4" />
              <AlertDescription>Este panel no muestra tokens, rutas internas ni enlaces de descarga.</AlertDescription>
            </Alert>
          </div>
        ) : (
          <div className="px-4 text-sm text-gray-600">No se pudo cargar el detalle de la ponencia.</div>
        )}
        <SheetFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="border-gray-200 shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-[#0f2744]">{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function DetailGrid({ rows }: { rows: Array<[string, string]> }) {
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label}>
          <p className="text-xs font-semibold uppercase text-gray-500">{label}</p>
          <p className="mt-1 break-words text-sm text-gray-800">{value}</p>
        </div>
      ))}
    </div>
  );
}

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-gray-200 bg-white p-3">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-lg font-bold text-[#0f2744]">{value}</p>
    </div>
  );
}
